<template>
  <div
    :class="rootClasses"
    :style="{ maxHeight: props.maxHeight }"
    tabindex="0"
    data-cy="ds-table"
  >
    <table
      ref="tableRef"
      :class="DS_TABLE_CLASSES.Table"
      :aria-label="props.ariaLabel"
      data-cy="ds-table__table"
    >
      <thead>
        <tr
          v-if="props.headerGroups?.length"
          data-cy="ds-table__groups"
        >
          <th
            v-for="group in displayedGroups"
            :key="`${group.key}-${group.startIndex}`"
            :colspan="group.colspan"
            :scope="group.label ? 'colgroup' : undefined"
            :aria-hidden="group.label ? undefined : 'true'"
            :class="groupHeaderClasses(group)"
            :style="stickyStyle(group.startIndex, group.endIndex)"
            :data-cy="group.label ? `ds-table__group--${group.key}` : undefined"
          >
            {{ group.label }}
          </th>
        </tr>
        <tr>
          <th
            v-for="(column, index) in displayedColumns"
            :key="column.key"
            scope="col"
            :class="[cellClasses(column, index), DS_TABLE_CLASSES.Header]"
            :style="{ width: column.width, minWidth: column.width, top: headerTop, ...stickyStyle(index) }"
            :aria-sort="ariaSort(column)"
            :data-cy="column.dataCy ?? `ds-table__header--${column.key}`"
          >
            <button
              v-if="column.sortable !== false"
              type="button"
              :class="DS_TABLE_CLASSES.Sort"
              :data-cy="`ds-table__sort--${column.key}`"
              @click="toggleSort(column)"
            >
              <slot
                :name="`header-${column.key}`"
                :column="column"
              >
                {{ column.label }}
              </slot>
              <DSIcon
                :class="sortIconClasses(column)"
                icon="triangle"
                size="xs"
                appearance="filled"
                aria-hidden="true"
              />
            </button>
            <slot
              v-else
              :name="`header-${column.key}`"
              :column="column"
              >{{ column.label }}</slot
            >
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in sortedRows"
          :key="getRowKey(row)"
          :data-cy="`ds-table__row--${getRowKey(row)}`"
        >
          <td
            v-for="(column, index) in displayedColumns"
            :key="column.key"
            :class="cellClasses(column, index)"
            :style="{ width: column.width, minWidth: column.width, ...stickyStyle(index) }"
            :data-cy="column.dataCy ? `${column.dataCy}-cell` : `ds-table__cell--${column.key}`"
          >
            <slot
              :name="`cell-${column.key}`"
              :row="row"
              :column="column"
              :value="row[column.key]"
              >{{ row[column.key] ?? "" }}</slot
            >
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { DSTableColumn, DSTableProps, DSTableRow } from "@/src/design_system/data_display/table/types/props";
import { DS_TABLE_CLASSES } from "@/src/design_system/data_display/table/constants/classes";
import { useDSTableSorting } from "@/src/design_system/data_display/table/composables/useDSTableSorting";
import { useDSTableLayout } from "@/src/design_system/data_display/table/composables/useDSTableLayout";
import { useDSTableStickyPositioning } from "@/src/design_system/data_display/table/composables/useDSTableStickyPositioning";
import { DSIcon } from "@/src/design_system";

const props = withDefaults(defineProps<DSTableProps>(), {
  ariaLabel: undefined,
  maxHeight: undefined,
  headerGroups: undefined,
  stickyHeader: false,
  stickyStartColumns: undefined,
  stickyEndColumns: undefined,
});
const { sortedRows, toggleSort, ariaSort } = useDSTableSorting(props);
const layout = useDSTableLayout(props);
const { displayedColumns, displayedGroups, cellClasses, groupHeaderClasses } = layout;
const { tableRef, headerTop, stickyStyle } = useDSTableStickyPositioning(props, layout);
const rootClasses = computed(() => [
  DS_TABLE_CLASSES.Root,
  {
    [DS_TABLE_CLASSES.StickyHeader]: props.stickyHeader,
  },
]);
const sortIconClasses = (column: DSTableColumn) => {
  const direction = ariaSort(column);
  return [
    DS_TABLE_CLASSES.SortIcon,
    {
      [DS_TABLE_CLASSES.SortIconActive]: !!direction,
      [DS_TABLE_CLASSES.SortIconAscending]: direction !== "descending",
    },
  ];
};
const getRowKey = (row: DSTableRow) => row[props.rowKey] as string | number;
</script>
